import React, { useEffect, useState, useCallback } from "react";
import { getKoachInfo, IKoachInfoResponse, toggleKoachFollow } from "api/student/v2/koach.api";
import { fetchContentList, IContent, toggleContentFavorite, toggleContentReaction } from "api/student/v2/content.api";
import { getCurrentLangName, getFlagByCountryCode, getGenderEmoji, getCurrentLanguageCode } from "helpers/String.helper";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import VideoPlayer from "commons/video/VideoPlayer";
import { usePopup } from "contexts/PopupContext";
import ContentCommentPopup from "./ContentCommentPopup";

interface KoachInfoPopupProps {
    koachId: string;
    onClose: () => void;
}

const KoachInfoPopup: React.FC<KoachInfoPopupProps> = ({ koachId, onClose }) => {
    // CSS 애니메이션 정의
    const fadeInUpAnimation = `
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(20px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes fadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
    `;
    const [koachInfo, setKoachInfo] = useState<IKoachInfoResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [isFollowLoading, setIsFollowLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'info' | 'class' | 'content'>('info');
    
    // 콘텐츠 탭 관련 상태
    const [contents, setContents] = useState<IContent[]>([]);
    const [isContentLoading, setIsContentLoading] = useState(false);
    const [contentError, setContentError] = useState<string | null>(null);
    const [hasLoadedContents, setHasLoadedContents] = useState(false);
    const [favoritingContentIds, setFavoritingContentIds] = useState<Set<number>>(new Set());
    const [reactingContentIds, setReactingContentIds] = useState<Set<number>>(new Set());
    const [showReactionTooltip, setShowReactionTooltip] = useState<number | null>(null);
    
    const { showPopup } = usePopup();

    useEffect(() => {
        const fetchKoachInfo = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const result = await getKoachInfo(koachId);
                if (result?.koach) {
                    setKoachInfo(result);
                    setIsFollowing((result.koach as any).isFollowing || false);
                } else {
                    setError("코치 정보를 찾을 수 없습니다.");
                }
            } catch (error) {
                console.error("코치 정보 조회 실패:", error);
                setError("코치 정보를 불러오는데 실패했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        if (koachId) {
            fetchKoachInfo();
        }
    }, [koachId]);

    const handleClose = () => {
        onClose();
    };

    const handleFollowToggle = async () => {
        if (isFollowLoading) return;
        
        try {
            setIsFollowLoading(true);
            const result = await toggleKoachFollow(koachId);
            console.log("팔로우 토글 결과:", result);
            console.log("isFollowing 값:", result?.isFollowing);
            
            // result.data가 있으면 그 안의 isFollowing 사용, 없으면 result.isFollowing 사용
            const newIsFollowing = (result as any)?.data?.isFollowing ?? result?.isFollowing;
            console.log("최종 isFollowing 값:", newIsFollowing);
            setIsFollowing(newIsFollowing);
        } catch (error) {
            console.error("팔로우 토글 실패:", error);
        } finally {
            setIsFollowLoading(false);
        }
    };

    // 콘텐츠 목록 불러오기
    const loadContents = useCallback(async () => {
        if (!koachInfo?.koach?.id) return;
        
        try {
            setIsContentLoading(true);
            setContentError(null);
            
            const response = await fetchContentList({
                koachId: koachInfo.koach.id,
                limit: 50
            });
            
            setContents(response.contentList);
            setHasLoadedContents(true);
        } catch (err: any) {
            console.error('콘텐츠 목록 조회 실패:', err);
            setContentError(err.message || '콘텐츠를 불러오는데 실패했습니다.');
        } finally {
            setIsContentLoading(false);
        }
    }, [koachInfo?.koach?.id]);

    // 콘텐츠 탭 선택 시 콘텐츠 로드
    useEffect(() => {
        if (activeTab === 'content' && !hasLoadedContents && koachInfo?.koach?.id) {
            loadContents();
        }
    }, [activeTab, hasLoadedContents, koachInfo?.koach?.id, loadContents]);

    // 콘텐츠 제목 가져오기
    const getContentTitle = (content: IContent): string => {
        if (content.title) return content.title;
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.title || content.multiTexts[0]?.title || '제목 없음';
        }
        return '제목 없음';
    };

    // 콘텐츠 설명 가져오기
    const getContentDesc = (content: IContent): string => {
        if (content.desc) return content.desc;
        if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            return currentMultiText?.desc || content.multiTexts[0]?.desc || '';
        }
        return '';
    };

    // 즐겨찾기 토글
    const handleFavoriteToggle = async (content: IContent, event: React.MouseEvent) => {
        event.stopPropagation();
        
        if (favoritingContentIds.has(content.id)) return;
        
        try {
            setFavoritingContentIds(prev => new Set(prev).add(content.id));
            const result = await toggleContentFavorite(content.id);
            
            setContents(prevContents => 
                prevContents.map(c => 
                    c.id === content.id 
                        ? { ...c, isFavorite: result.isFavorite, favoriteCount: result.favoriteCount }
                        : c
                )
            );
        } catch (error) {
            console.error("즐겨찾기 토글 실패:", error);
        } finally {
            setFavoritingContentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(content.id);
                return newSet;
            });
        }
    };

    // 리액션 버튼 클릭
    const handleReactionClick = (contentId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setShowReactionTooltip(showReactionTooltip === contentId ? null : contentId);
    };

    // 리액션 선택
    const handleReactionSelect = async (contentId: number, reactionType: string, event: React.MouseEvent) => {
        event.stopPropagation();
        
        if (reactingContentIds.has(contentId)) return;
        
        try {
            setReactingContentIds(prev => new Set(prev).add(contentId));
            const result = await toggleContentReaction(contentId, reactionType);
            
            setContents(prevContents => 
                prevContents.map(c => 
                    c.id === contentId 
                        ? { 
                            ...c, 
                            myReactionTypeCode: result.isReactionActive ? result.reactionTypeCode : null,
                            reactionCount: result.reactionCount 
                        }
                        : c
                )
            );
            setShowReactionTooltip(null);
        } catch (error) {
            console.error("리액션 토글 실패:", error);
        } finally {
            setReactingContentIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(contentId);
                return newSet;
            });
        }
    };

    // 댓글 버튼 클릭
    const handleCommentClick = (content: IContent, event: React.MouseEvent) => {
        event.stopPropagation();
        
        const popupContent = (
            <ContentCommentPopup
                contentId={content.id}
                contentTitle={getContentTitle(content)}
                contentDesc={getContentDesc(content)}
                onClose={() => showPopup(null)}
                onRefresh={() => loadContents()}
            />
        );
        showPopup(popupContent);
    };

    // 리액션 툴팁 외부 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = () => {
            if (showReactionTooltip !== null) {
                setShowReactionTooltip(null);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showReactionTooltip]);

    // 로딩 상태
    if (isLoading) {
        return (
            <>
                <style>{fadeInUpAnimation}</style>
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    style={{
                        animation: 'fadeIn 0.4s ease-out forwards',
                        opacity: 0
                    }}
                >
                    <div 
                        className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto"
                        style={{
                            animation: 'fadeInUp 0.4s ease-out forwards',
                            transform: 'translateY(20px)',
                            opacity: 0
                        }}
                    >
                    <div className="animate-pulse p-4">
                        {/* 헤더 스켈레톤 */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                                <div>
                                    <div className="h-3 bg-gray-200 rounded w-20 mb-1"></div>
                                    <div className="h-2 bg-gray-200 rounded w-16"></div>
                                </div>
                            </div>
                            <div className="w-5 h-5 bg-gray-200 rounded"></div>
                        </div>

                        {/* 제목 스켈레톤 */}
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>

                        {/* 내용 스켈레톤 */}
                        <div className="space-y-2 mb-4">
                            <div className="h-3 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>

                        {/* 통계 스켈레톤 */}
                        <div className="space-y-3">
                            <div className="h-3 bg-gray-200 rounded w-12 mb-2"></div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="h-12 bg-gray-200 rounded-lg"></div>
                                <div className="h-12 bg-gray-200 rounded-lg"></div>
                                <div className="h-12 bg-gray-200 rounded-lg"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
        );
    }

    // 에러 상태
    if (error || !koachInfo?.koach) {
        return (
            <>
                <style>{fadeInUpAnimation}</style>
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    style={{
                        animation: 'fadeIn 0.4s ease-out forwards',
                        opacity: 0
                    }}
                >
                    <div 
                        className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-4"
                        style={{
                            animation: 'fadeInUp 0.4s ease-out forwards',
                            transform: 'translateY(20px)',
                            opacity: 0
                        }}
                    >
                    <div className="text-center">
                        <div className="text-red-500 text-4xl mb-3">⚠️</div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">
                            코치 정보 로드 실패
                        </h3>
                        <p className="text-gray-600 text-sm mb-3">
                            {error || "코치 정보를 불러올 수 없습니다."}
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
                        >
                            닫기
                        </button>
                    </div>
                </div>
            </div>
        </>
        );
    }

    const info = koachInfo.koach;

    // 프로필 이미지 URL 생성
    let profileImageUrl = '/imgs/dummy/basic-img.png';
    let profileImageUrlForCircle = '/imgs/dummy/basic-img.png';
    if (info.profileImageInfo?.url) {
        profileImageUrl = info.profileImageInfo.url;
        profileImageUrlForCircle = info.profileImageInfo.url;
    } else if (info.profileImage) {
        profileImageUrl = getImageUrl(info.profileImage, 800, 400);
        profileImageUrlForCircle = getImageUrl(info.profileImage, 200, 200);
    }

    // 배경 이미지 URL 생성
    let bgImageUrl = null;
    if (info.bgImageInfo?.url) {
        bgImageUrl = info.bgImageInfo.url;
    } else if (info.bgImageId) {
        bgImageUrl = getImageUrl(info.bgImageId, 1200, 600);
    }

    // 배경 색상
    const bgColorHexCode = info.bgColorHexCode || null;

    // 국가 코드
    const countryCode = info.country?.code || info.countryCode || '';
    
    // 성별 코드
    const genderTypeCode = info.genderTypeCode || '';

    // 평균 평점 (10점 만점을 5점 만점으로 변환)
    const averageRating = (info.averageRating !== null && info.averageRating !== undefined) 
        ? Number(info.averageRating) 
        : null;

    // 구사 가능한 언어 (API 응답에 포함되어 있을 수 있음)
    const availableLanguageList = (info as any).availableLanguageList || [];
    const availableLanguages = (info as any).availableLanguages || [];

    // 전화번호 조합
    const phoneText = info.countryPhoneCode && info.cellphone
        ? `+${info.countryPhoneCode} ${info.cellphone}`
        : info.cellphone || '';

    return (
        <>
            <style>{fadeInUpAnimation}</style>
            <div 
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                style={{
                    animation: 'fadeIn 0.4s ease-out forwards',
                    opacity: 0
                }}
                onClick={handleClose}
            >
                <div 
                    className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                    style={{
                        animation: 'fadeInUp 0.4s ease-out forwards',
                        transform: 'translateY(20px)',
                        opacity: 0
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* 플로팅 닫기 버튼 - 팝업 컨테이너 기준 고정 위치 */}
                    <button 
                        onClick={handleClose}
                        className="absolute top-4 right-4 z-30 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
                        aria-label="닫기"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* 스크롤 가능한 콘텐츠 영역 */}
                    <div className="flex-1 overflow-y-auto">
                        {/* 프로필 카드 */}
                        <div className="bg-white rounded-lg shadow-sm overflow-visible relative">
                            {/* 프로필 헤더 (배경 이미지 우선, 없으면 컬러) */}
                            <div className="relative h-64 md:h-80 overflow-visible rounded-t-lg">
                            {/* 배경 */}
                            <div className="absolute inset-0 overflow-hidden rounded-t-lg">
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        ...(bgImageUrl
                                            ? { backgroundImage: `url(${bgImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                                            : bgColorHexCode
                                                ? { backgroundColor: `#${bgColorHexCode}` }
                                                : { background: 'linear-gradient(to right, #CF3688, #B52E74)' }
                                        )
                                    }}
                                />
                                {/* 배경 이미지 오버레이 (가독성을 위한 어두운 그라데이션) */}
                                {bgImageUrl && (
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
                                )}
                            </div>

                            {/* 하단 그라데이션 배경 및 정보 영역 */}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent pt-16 pb-6 px-6">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                    <div className="flex flex-col gap-2">
                                        {/* 닉네임과 국적 깃발 */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-2xl md:text-3xl font-bold text-white">
                                                    {info.nickname || info.firstName + ' ' + info.lastName}
                                                </h2>
                                            </div>
                                            {countryCode && (
                                                <span className="text-2xl md:text-3xl">
                                                    {getFlagByCountryCode(countryCode)}
                                                </span>
                                            )}
                                        </div>

                                        {/* 코치 유형과 성별 (한 줄) */}
                                        <div className="flex items-center gap-3 text-lg md:text-xl text-white/95 font-medium">
                                            {info.koachType && (
                                                <span>{getCurrentLangName(info.koachType.names)}</span>
                                            )}
                                            {info.koachType && genderTypeCode && (
                                                <span className="text-white/60">•</span>
                                            )}
                                            {genderTypeCode && (
                                                <span className="text-xl md:text-2xl">
                                                    {getGenderEmoji(genderTypeCode)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                                {/* 원형 프로필 이미지 (헤더 하단 경계에 반쯤 걸치게 위치) */}
                                <div className="absolute bottom-0 right-6 z-10" style={{ transform: 'translateY(50%)' }}>
                                    <div className="relative">
                                        <img
                                            src={profileImageUrlForCircle}
                                            alt="프로필"
                                            className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white shadow-lg"
                                        />
                                    </div>
                                </div>
                            </div>

                        {/* 평균 별점 (헤더 바로 아래) */}
                        {averageRating !== null && (
                            <div className="px-6 pt-16 md:pt-20 pb-4 border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: 5 }, (_, i) => {
                                            // 10점 만점을 5점 만점으로 변환하고 0.5 단위로 반올림
                                            const rating5 = Math.round((averageRating / 2) * 2) / 2;
                                            const filled = i < rating5;
                                            return (
                                                <span
                                                    key={i}
                                                    className={`text-xl ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
                                                >
                                                    ★
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <span className="text-lg font-medium text-gray-900">
                                        {Math.round((averageRating / 2) * 2) / 2}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* 탭별 콘텐츠 영역 */}
                        <div className="p-6 space-y-6">
                            {/* 정보 탭 */}
                            {activeTab === 'info' && (
                                <>
                                    {/* 기본 정보 */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* 핸들러 */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                핸들러
                                            </label>
                                            <p className="text-gray-900">{info.handler ? `@${info.handler}` : '-'}</p>
                                        </div>

                                        {/* 구사 가능한 언어 */}
                                        {availableLanguageList.length > 0 && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    구사 가능한 언어
                                                </label>
                                                <div className="space-y-2">
                                                    <ul className="space-y-2">
                                                        {availableLanguageList.map((lang: any, index: number) => {
                                                            const languageName = availableLanguages[index] || '';
                                                            const levelText = lang.level === 1 ? '기본 대화 가능' : 
                                                                             lang.level === 2 ? '코칭 피드백 가능' : 
                                                                             lang.level === 3 ? '라이브 코칭 가능' : '';
                                                            return (
                                                                <li key={lang.languageCode} className="flex items-center justify-between">
                                                                    <span className="text-gray-900 flex items-center">
                                                                        <span className="mr-2">•</span>
                                                                        {languageName}
                                                                    </span>
                                                                    <span className="text-sm text-gray-500 ml-2">{levelText}</span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Koach 소개 */}
                                    {info.introduction && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Koach 소개
                                            </label>
                                            <p className="text-gray-900 whitespace-pre-line">{info.introduction}</p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* 클래스 탭 */}
                            {activeTab === 'class' && (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <p className="text-gray-500 text-sm">클래스 목록이 없습니다.</p>
                                </div>
                            )}

                            {/* 콘텐츠 탭 */}
                            {activeTab === 'content' && (
                                <>
                                    <h2 className="text-lg font-bold text-gray-900 mb-4">콘텐츠</h2>
                                    {isContentLoading ? (
                                        <div className="space-y-6">
                                            {/* 콘텐츠 스켈레톤 3개 */}
                                            {[1, 2, 3].map((i) => (
                                                <div key={i} className="relative bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
                                                    {/* 헤더 스켈레톤 */}
                                                    <div className="p-4 border-b border-gray-100">
                                                        <div className="flex items-center justify-between">
                                                            <div className="h-5 bg-gray-200 rounded w-2/3"></div>
                                                            <div className="h-4 bg-gray-200 rounded w-20"></div>
                                                        </div>
                                                    </div>
                                                    {/* 콘텐츠 스켈레톤 */}
                                                    <div className="p-4">
                                                        {/* 설명 스켈레톤 */}
                                                        <div className="space-y-2 mb-4">
                                                            <div className="h-3 bg-gray-200 rounded w-full"></div>
                                                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                                                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                                                        </div>
                                                        {/* 비디오 스켈레톤 */}
                                                        <div className="h-40 bg-gray-200 rounded-lg mb-4"></div>
                                                        {/* 액션 바 스켈레톤 */}
                                                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                                            <div className="flex items-center space-x-4">
                                                                <div className="h-5 bg-gray-200 rounded w-12"></div>
                                                                <div className="h-5 bg-gray-200 rounded w-12"></div>
                                                            </div>
                                                            <div className="h-5 bg-gray-200 rounded w-12"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : contentError ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <p className="text-red-500 text-sm">{contentError}</p>
                                        </div>
                                    ) : contents.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <p className="text-gray-500 text-sm">등록된 콘텐츠가 없습니다.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-6">
                                            {contents.map((content) => (
                                                <div 
                                                    key={content.id}
                                                    className="relative bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200"
                                                >
                                                    {/* 콘텐츠 헤더 */}
                                                    <div className="p-4 border-b border-gray-100">
                                                        <div className="flex items-center justify-between">
                                                            <h3 className="text-base font-bold text-gray-900 break-words overflow-hidden">
                                                                {getContentTitle(content)}
                                                            </h3>
                                                            <p className="text-sm text-gray-500 flex-shrink-0 ml-2">
                                                                {new Date(content.createdAt).toLocaleDateString('ko-KR')}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* 콘텐츠 내용 */}
                                                    <div className="p-4">
                                                        {/* 설명 */}
                                                        {getContentDesc(content) && (
                                                            <div className="mb-4">
                                                                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap break-words">
                                                                    {getContentDesc(content)}
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* 비디오 */}
                                                        {content.video && (
                                                            <div className="mb-4">
                                                                <VideoPlayer
                                                                    video={content.video}
                                                                    className="w-full rounded-lg border border-gray-200"
                                                                    enableVideoLog={false}
                                                                />
                                                            </div>
                                                        )}

                                                        {/* 하단 액션 바: Reaction, 댓글, 즐겨찾기 */}
                                                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                                            {/* 왼쪽: Reaction과 댓글 */}
                                                            <div className="flex items-center space-x-4">
                                                                {/* Reaction */}
                                                                <div className="relative">
                                                                    <button
                                                                        onClick={(e) => handleReactionClick(content.id, e)}
                                                                        className={`flex items-center space-x-1 transition-colors cursor-pointer ${
                                                                            content.myReactionTypeCode 
                                                                                ? 'text-[#CF3688]' 
                                                                                : 'text-gray-500 hover:text-[#CF3688]'
                                                                        }`}
                                                                    >
                                                                        {content.myReactionTypeCode ? (
                                                                            <img 
                                                                                src={
                                                                                    content.myReactionTypeCode === 'reactionType:veryHelpful'
                                                                                        ? '/imgs/icons/icon-reaction-very-helpful-active.svg'
                                                                                        : content.myReactionTypeCode === 'reactionType:helpful'
                                                                                        ? '/imgs/icons/icon-reaction-helpful-active.svg'
                                                                                        : content.myReactionTypeCode === 'reactionType:disappointed'
                                                                                        ? '/imgs/icons/icon-reaction-disappointed-active.svg'
                                                                                        : '/imgs/icons/icon-reaction-very-helpful.svg'
                                                                                }
                                                                                alt="리액션"
                                                                                className="w-5 h-5"
                                                                            />
                                                                        ) : (
                                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                            </svg>
                                                                        )}
                                                                        <span className="text-sm font-medium">
                                                                            {content.reactionCount || 0}
                                                                        </span>
                                                                    </button>
                                                                    
                                                                    {/* 리액션 선택 툴팁 */}
                                                                    {showReactionTooltip === content.id && (
                                                                        <div 
                                                                            className="absolute bottom-full left-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-10"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <button
                                                                                onClick={(e) => handleReactionSelect(content.id, 'reactionType:veryHelpful', e)}
                                                                                className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                                                    content.myReactionTypeCode === 'reactionType:veryHelpful'
                                                                                        ? 'bg-[#CF3688]/10'
                                                                                        : 'hover:bg-gray-100'
                                                                                }`}
                                                                                title="큰 도움이 됐어요"
                                                                            >
                                                                                <img 
                                                                                    src={content.myReactionTypeCode === 'reactionType:veryHelpful' 
                                                                                        ? '/imgs/icons/icon-reaction-very-helpful-active.svg'
                                                                                        : '/imgs/icons/icon-reaction-very-helpful.svg'
                                                                                    }
                                                                                    alt="큰 도움이 됐어요"
                                                                                    className="w-6 h-6"
                                                                                />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => handleReactionSelect(content.id, 'reactionType:helpful', e)}
                                                                                className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                                                    content.myReactionTypeCode === 'reactionType:helpful'
                                                                                        ? 'bg-[#CF3688]/10'
                                                                                        : 'hover:bg-gray-100'
                                                                                }`}
                                                                                title="도움이 됐어요"
                                                                            >
                                                                                <img 
                                                                                    src={content.myReactionTypeCode === 'reactionType:helpful' 
                                                                                        ? '/imgs/icons/icon-reaction-helpful-active.svg'
                                                                                        : '/imgs/icons/icon-reaction-helpful.svg'
                                                                                    }
                                                                                    alt="도움이 됐어요"
                                                                                    className="w-6 h-6"
                                                                                />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => handleReactionSelect(content.id, 'reactionType:disappointed', e)}
                                                                                className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                                                                    content.myReactionTypeCode === 'reactionType:disappointed'
                                                                                        ? 'bg-[#CF3688]/10'
                                                                                        : 'hover:bg-gray-100'
                                                                                }`}
                                                                                title="아쉬웠어요"
                                                                            >
                                                                                <img 
                                                                                    src={content.myReactionTypeCode === 'reactionType:disappointed' 
                                                                                        ? '/imgs/icons/icon-reaction-disappointed-active.svg'
                                                                                        : '/imgs/icons/icon-reaction-disappointed.svg'
                                                                                    }
                                                                                    alt="아쉬웠어요"
                                                                                    className="w-6 h-6"
                                                                                />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* 댓글 */}
                                                                <button
                                                                    onClick={(e) => handleCommentClick(content, e)}
                                                                    className="flex items-center space-x-1 text-gray-500 hover:text-[#CF3688] transition-colors cursor-pointer"
                                                                >
                                                                    <img 
                                                                        src="/imgs/icons/icon-comment.svg" 
                                                                        alt="댓글" 
                                                                        className="w-5 h-5"
                                                                    />
                                                                    <span className="text-sm font-medium">
                                                                        {content.commentCount || 0}
                                                                    </span>
                                                                </button>
                                                            </div>

                                                            {/* 오른쪽: 즐겨찾기 */}
                                                            <button
                                                                onClick={(e) => handleFavoriteToggle(content, e)}
                                                                disabled={favoritingContentIds.has(content.id)}
                                                                className={`flex items-center space-x-1 transition-all duration-200 ${
                                                                    favoritingContentIds.has(content.id) 
                                                                        ? 'opacity-50 cursor-not-allowed' 
                                                                        : content.isFavorite 
                                                                            ? 'text-[#CF3688]' 
                                                                            : 'text-gray-500'
                                                                }`}
                                                            >
                                                                {favoritingContentIds.has(content.id) ? (
                                                                    <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                ) : (
                                                                    <img
                                                                        src={content.isFavorite ? "/imgs/icons/icon-favorite-active.svg" : "/imgs/icons/icon-favorite.svg"}
                                                                        alt="즐겨찾기"
                                                                        className="w-5 h-5"
                                                                    />
                                                                )}
                                                                <span className="text-sm font-medium">
                                                                    {content.favoriteCount || 0}
                                                                </span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        </div>
                    </div>

                    {/* 하단 탭 버튼 + 팔로우 버튼 - 항상 고정 */}
                    <div className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
                        <div className="flex items-center gap-2">
                            {/* 탭 버튼들 */}
                            <div className="flex-1 flex items-center">
                                <button
                                    onClick={() => setActiveTab('info')}
                                    className={`flex-1 py-2 text-sm transition-all border-b-2 ${
                                        activeTab === 'info'
                                            ? 'text-[#CF3688] font-bold border-[#CF3688]'
                                            : 'text-gray-400 font-normal border-transparent hover:text-gray-600'
                                    }`}
                                >
                                    정보
                                </button>
                                <button
                                    onClick={() => setActiveTab('class')}
                                    className={`flex-1 py-2 text-sm transition-all border-b-2 ${
                                        activeTab === 'class'
                                            ? 'text-[#CF3688] font-bold border-[#CF3688]'
                                            : 'text-gray-400 font-normal border-transparent hover:text-gray-600'
                                    }`}
                                >
                                    클래스
                                </button>
                                <button
                                    onClick={() => setActiveTab('content')}
                                    className={`flex-1 py-2 text-sm transition-all border-b-2 ${
                                        activeTab === 'content'
                                            ? 'text-[#CF3688] font-bold border-[#CF3688]'
                                            : 'text-gray-400 font-normal border-transparent hover:text-gray-600'
                                    }`}
                                >
                                    콘텐츠
                                </button>
                            </div>

                            {/* 팔로우 버튼 */}
                            <button
                                onClick={handleFollowToggle}
                                disabled={isFollowLoading}
                                className={`w-12 h-10 rounded-lg transition-all flex items-center justify-center ${
                                    isFollowing
                                        ? 'bg-gray-100 hover:bg-gray-200'
                                        : 'bg-[#CF3688] hover:bg-[#B52E74]'
                                } ${isFollowLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {isFollowLoading ? (
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : isFollowing ? (
                                    <img 
                                        src="/imgs/icons/icon-unfollow.svg" 
                                        alt="언팔로우" 
                                        className="w-5 h-5"
                                    />
                                ) : (
                                    <img 
                                        src="/imgs/icons/icon-follow-filled.svg" 
                                        alt="팔로우" 
                                        className="w-5 h-5"
                                    />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default KoachInfoPopup;
